import styled from "styled-components";
import {
  FocusContext,
  updateAllLayouts,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { ShowCaseComponentSwitch } from "../showcase/showcase.utils";
import smoothScroll from "../../utils/animateScroll";
import httpService from "../../services/http.service";
import { getCMSApiRoute } from "../../utils/api.utils";
import { anavatanCities } from "../../config/enums";
import { MoviesSlider } from "./moviesList";
import LazyRow from "../common/LazyRow";
import { FONT_FAMILY, GLOBAL_COLORS } from "../../config/globalStyles";
import { useInputMode } from "../../utils/hooks/useInputMode";

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const ContentWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ScrollingRows = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: 100vh;
  width: 100%;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 20vh;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: #EF4444;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyStateBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 90%;
  margin: 2.5rem auto 0;
  min-height: 10vh;
  background: rgb(39, 39, 44);
  border-radius: 12px;
`;

const EmptyStateText = styled.p`
  color: white;
  font-size: 24px;
  margin: 0;
  font-family: ${FONT_FAMILY};
`;

const DisabledHeadButton = styled.button`
  background-color: #434343;
  font-size: 24px;
  font-weight: 400;
  line-height: 20px;
  color: #D9D9D9;
  border-radius: 20px;
  padding: 1.5rem 2rem;
  margin-left: 1.5%;
  text-align: center;
  border: none;
  min-width: fit-content;
  cursor: default;
  font-family: ${FONT_FAMILY};
  opacity: 0.55;
  pointer-events: none;
  user-select: none;
`;

const DisabledContentRowScrollingWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 1;
  flex-grow: 1;
  padding-left: 60px;
  pointer-events: none;
`;

const DisabledContentRowScrollingContent = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 2rem;
`;

const DisabledContentRowWrapper = styled.div``;

const DisabledHeadContainer = styled.div`
  margin-top: 2.5rem;
  padding: 1.5rem;
  margin-left: auto;
  margin-right: auto;
  width: calc(100% - 60px);
  border-radius: 24px;
  background: #202020;
  opacity: 0.72;
  user-select: none;
`;

const DisabledHeadTitleRow = styled.div`
  display: flex;
  align-items: center;
`;

const DisabledHeadTitle = styled.h2`
  font-size: 32px;
  font-weight: 600;
  color: #F0F0F0;
  margin: 0;
  font-family: ${FONT_FAMILY};
`;

const DisabledHeadSubtitle = styled.p`
  font-size: 22px;
  font-weight: 500;
  color: #BFBFBF;
  margin: 0;
  font-family: ${FONT_FAMILY};
`;

const DisabledIranIcon = styled.img`
  margin-left: 0.75rem;
`;

const DisabledHeadContent = ({ states }) => (
  <DisabledHeadContainer aria-disabled="true">
    <DisabledHeadTitleRow>
      <DisabledIranIcon src="iran.svg" alt="iran" draggable="false" />
      <DisabledHeadTitle>آناوطن</DisabledHeadTitle>
    </DisabledHeadTitleRow>
    <DisabledHeadSubtitle>محتوای استان خود را تماشا کنید.</DisabledHeadSubtitle>
    <DisabledContentRowWrapper>
      <DisabledContentRowScrollingWrapper>
        <DisabledContentRowScrollingContent>
          {states?.map((state) => (
            <DisabledHeadButton
              type="button"
              disabled
              tabIndex={-1}
              className={state.style}
              key={state.id}
            >
              {state.name}
            </DisabledHeadButton>
          ))}
        </DisabledContentRowScrollingContent>
      </DisabledContentRowScrollingWrapper>
    </DisabledContentRowWrapper>
  </DisabledHeadContainer>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AnavatanContent = ({ showCaseData = {} }) => {
  const { ref, focusKey } = useFocusable({
    focusKey: "anavatan_content",
  });
  const [selectedCity, setSelectedCity] = useState(null);
  const [cityData, setCityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const skipNextScrollRef = React.useRef(false);

  useEffect(() => {
    if (selectedCity && selectedCity !== "default") {
      setLoading(true);
      // Set flag to skip scroll on next focus after data loads
      skipNextScrollRef.current = true;
      const fetchCityData = async () => {
        try {
          const response = await httpService.get(
            getCMSApiRoute("province-content"),
            {
              params: {
                province: selectedCity,
              },
            }
          );

          const data = response.data?.data;

          let res = [];

          for (let city of data) {
            if (city.contents?.length > 0) {
              res.push({
                ...city,
                contents: city.contents?.reduce(
                  (acc, i) => acc.concat({ content: i }),
                  []
                ),
              });
            }
          }
          setCityData(res); // Set fetched data
        } catch (error) {
          setCityData([]); // Set empty array if error
          console.error("Error fetching city data:", error);
        } finally {
          setLoading(false);
          updateAllLayouts();
        }
      };

      fetchCityData();
    }
  }, [selectedCity]);

  // Reset scroll to top when cityData changes (new content loaded)
  useEffect(() => {
    if (cityData && ref.current) {
      ref.current.scrollTop = 0;
    }
  }, [cityData, ref]);

  const { isMouseFocus } = useInputMode();
  const onRowFocus = useCallback(
    (props) => {
      if (isMouseFocus) return;
      const { y, height } = props;
      const targetScroll = y > height - 200 ? y : 0;

      smoothScroll(ref, targetScroll, 500);
    },
    [ref, isMouseFocus]
  );

  const data = useMemo(() => {
    let results = [];

    Object.keys(showCaseData)?.map((key) =>
      showCaseData[key]?.map((item) => results.push(item))
    );

    return _.sortBy(results, (x) => x.priority);
  }, [showCaseData]);

  return (
    <ContentWrapper>
      <FocusContext.Provider value={focusKey}>
        <ScrollingRows ref={ref}>
          <div>
            <DisabledHeadContent
              states={anavatanCities}
              onFocus={onRowFocus}
              setSelectedCity={setSelectedCity}
              selectedCity={selectedCity}
            />
            {/* {loading && (
              <LoadingContainer>
                <Spinner />
              </LoadingContainer>
            )}
            {!loading && (
              <div>
                {(data?.length > 0 && !selectedCity) ||
                  selectedCity === "default" ? (
                  data?.map((item, index) => (
                    <ShowCaseComponentSwitch
                      key={"sc_av_" + item._id}
                      item={item}
                      onRowFocus={onRowFocus}
                      parentIndex={index}
                    />
                  ))
                ) : cityData && cityData?.length > 0 ? (
                  cityData?.map(
                    (item, index) =>
                      item.contents.length > 0 && (
                        <LazyRow key={'city_row_' + index} index={index}>
                          <MoviesSlider
                            movies={item.contents}
                            onFocus={onRowFocus}
                            rowTitle={item.name}
                            shapeType={"movie"}
                            id={'city_row_' + index}
                            index={index}
                            cityKey={item.name}
                            provinceKey={selectedCity !== "default" ? selectedCity : null}
                          />
                        </LazyRow>
                      )
                  )
                ) : (
                  <EmptyStateBox>
                    <EmptyStateText>محتوایی یافت نشد.</EmptyStateText>
                  </EmptyStateBox>
                )}
              </div>
            )} */}

            <div className="flex flex-col justify-center items-center w-full min-h-[50vh] text-white">
              <h4 className="text-2xl font-medium">به زودی ...</h4>
              <p>بخش آناوطن به زودی در دسترس خواهد بود</p>
            </div>
          </div>
        </ScrollingRows>
      </FocusContext.Provider>
    </ContentWrapper>
  );
};

export default AnavatanContent;
